import {
  costStatisticsTypes,
  EMPTY_STATISTICS_BUNDLE_BUILDER_OPTIONS,
  quantityStatisticsTypes,
  SettlementEntity,
  StatisticsSearchCriteria,
  statisticsSelector,
  statisticsType,
  transportMethod,
  useSearchSettlementStatisticsQuery
} from "@ots-frontend/ots-data";
import {useMemo} from "react";
import {Card, Divider, Grid, Group, Stack, Text, Title} from "@mantine/core";
import {IconChartDots3, IconTruck} from "@tabler/icons-react";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {StatisticsDataTable} from "../../data-table/StatisticsDataTable/StatisticsDataTable";
import {toStatisticsDataGroups} from "../../data-table/StatisticsDataTable/useStatisticsDataTableColumns";
import { LineByTimeInsight } from "../../insights/ByTimeInsight/LineByTimeInsight";

export type SettlementInsightsProps = {
  settlement: SettlementEntity
}

export const SettlementInsights = (props: SettlementInsightsProps) => {
  const {settlement} = props;

  const {appCategory, regionId} = useApp()

  const criteria: StatisticsSearchCriteria = useMemo(() => ({
    regionIds: regionId ? [regionId] : undefined,
    categories: appCategory ? [appCategory.code] : undefined,
    includeSubTruckCompanies: true,
    includeTruckingData: true,
    includePipelineData: true,
  }), [regionId, appCategory])

  const selectStatistics = useMemo(() => {
    return statisticsSelector()
  }, []);

  const {statisticsBundle, isLoading} = useSearchSettlementStatisticsQuery({
    ...criteria,
    settlementId: settlement.id
  }, {
    selectFromResult: (result) => ({
      statisticsBundle: selectStatistics(result, {
        ...result,
        ...EMPTY_STATISTICS_BUNDLE_BUILDER_OPTIONS,
        processRegion: true,
        processTruckCompany: true,
        processVendorCompany: true,
      }),
      isLoading: result.isLoading || result.isFetching,
    })
  });

  return (
    <Grid>
      <Grid.Col span={12}>
        <Card shadow="sm" padding="lg" mb="lg" radius="md">
          <Stack>
            <Title order={3}>
              Trucking Settlement
            </Title>
            <StatisticsDataTable
              withBorder
              scrollAreaProps={{
                offsetScrollbars: true
              }}
              method={transportMethod.trucking}
              types={[
                statisticsType.quantity,
                statisticsType.netQuantity,
                statisticsType.billableQuantity,
                statisticsType.billableTime,
                statisticsType.demurrageTimeCost,
                statisticsType.ticketRejectLoadCost,
                statisticsType.fuelSurchargeCost,
                statisticsType.timeCost,
                statisticsType.loadCost,
                statisticsType.freightCost,
                statisticsType.cost
              ]}
              category={appCategory}
              records={statisticsBundle?.truckCompanyStats}
              recordsPerPageOptions={[10]}
              fetching={isLoading}
              hideExpansion={true}
              showTotals={true}/>
          </Stack>
        </Card>
      </Grid.Col>
      {settlement.activityPeriodFromDttm &&
        <>
          <Grid.Col span={6}>
            <LineByTimeInsight
              id="settlement_cost_by_time"
              title={"Cost By Time"}
              fromDttm={settlement.activityPeriodFromDttm}
              toDttm={settlement.activityPeriodToDttm}
              statisticsTypes={costStatisticsTypes}
              data={statisticsBundle?.regionStats}
              methods={transportMethod.trucking}
              category={settlement.activityPeriodCategory}/>
          </Grid.Col>
          <Grid.Col span={6}>
            <LineByTimeInsight
              id="settlement_quantity_by_time"
              title={"Quantity By Time"}
              fromDttm={settlement.activityPeriodFromDttm}
              toDttm={settlement.activityPeriodToDttm}
              statisticsTypes={quantityStatisticsTypes}
              data={statisticsBundle?.regionStats}
              methods={transportMethod.trucking}
              category={settlement.activityPeriodCategory}/>
          </Grid.Col>
        </>
      }
      {statisticsBundle?.disposalStats?.map((stats, index) => {
        return (
          <Grid.Col span={12} key={index}>
            <Divider my="sm"/>
            <Card shadow="sm" padding="lg" mb="lg" radius="md" withBorder>
              <Stack>
                <Title order={3}>
                  {stats.name + ": Disposal Settlement"}
                </Title>
                <Group mt="md" spacing="xs">
                  <IconTruck size={16}/>
                  <Text>Trucking</Text>
                </Group>
                <StatisticsDataTable
                  withBorder
                  withColumnBorders
                  scrollAreaProps={{
                    offsetScrollbars: true
                  }}
                  method={transportMethod.trucking}
                  types={[
                    statisticsType.quantity,
                    statisticsType.netQuantity,
                  ]}
                  category={appCategory}
                  records={Object.values(stats.truckCompanyMap)}
                  grouping={{
                    groupRetriever: (data) => data.siteMap,
                    groups: toStatisticsDataGroups(Object.values(stats.truckCompanyMap)?.map((subStats) => subStats.siteMap))
                  }}
                  recordsPerPageOptions={[10]}
                  fetching={isLoading}
                  hideExpansion={true}
                  showTotals={true}/>
                <Group mt="md" spacing="xs">
                  <IconChartDots3 size={16}/>
                  <Text>Pipeline</Text>
                </Group>
                <StatisticsDataTable
                  withBorder
                  withColumnBorders
                  scrollAreaProps={{
                    offsetScrollbars: true
                  }}
                  method={transportMethod.pipeline}
                  types={[
                    statisticsType.quantity
                  ]}
                  category={appCategory}
                  records={Object.values(stats.pipelineMap)}
                  grouping={{
                    groupRetriever: (data) => data.siteMap,
                    groups: toStatisticsDataGroups(Object.values(stats.pipelineMap)?.map((subStats) => subStats.siteMap))
                  }}
                  recordsPerPageOptions={[10]}
                  fetching={isLoading}
                  hideExpansion={true}
                  showTotals={true}/>
              </Stack>
            </Card>
          </Grid.Col>
        )
      })}
    </Grid>
  );
};
